import * as React from 'react';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useAgyleConfigStyles} from '../agyle_configuration';
import {Paper} from '@mui/material';
import {defaultEcdsVersion, getEcdsVersion, setDischargeTemplate} from '../../../store/reducers/dicts';
import {useDispatch, useSelector} from 'react-redux';
import AgyleButton from '../../../components/ui/buttons/agyleButton';
import AddIcon from '@mui/icons-material/Add';
import AgyleSelectField from '../../../components/ui/selector/agyleSelectField';
import API from '../../../app/api';
import {setSnackbar} from '../../../store/reducers/globals';
import {AgyleTable, createAgyleTableColumn} from '../../../components/table/AgyleTable';
import DischargeTemplateModal from './components/dischargeTemplateModal';
import useOpenState from '../../../hooks/useOpenState';
import AgyleLoadingSpinner from '../../../components/other/agyleLoadingSpinner';
import MessageBox from '../../../util/messageBox';
import {ECDS_VERSION} from '../../ward/forms/formsUtil';
import {cloneDeep} from 'lodash';
import moment from 'moment/moment';
import AgyleSwitch from '../../../components/ui/switch/agyleSwitch';

const columnsTable = [
    createAgyleTableColumn('no', 'No.', 1, null, 'count', 'notSort', 'medium', 'center', true, false, null, null, null),
    createAgyleTableColumn('name', 'Description', 2, 'description', 'string', '', 'medium', 'center', true, true, null, null, null),
    createAgyleTableColumn('clinician', 'Clinician', 3, 'clinician', 'dict', '', 'medium', 'center', true, true, null, null, null),
    createAgyleTableColumn('dischargeDestination', 'Discharge Destination', 4, 'dischargeDestination', 'dict', '', 'medium', 'center', true, true, null, null, null),
    createAgyleTableColumn('dischargeFollowUp', 'Discharge Follow Up', 5, 'dischargeFollowUp', 'dict', '', 'medium', 'center', true, true, null, null, null),
    createAgyleTableColumn('dischargeStatus', 'Discharge Status', 6, 'dischargeStatus', 'dict', '', 'medium', 'center', true, true, null, null, null),
    createAgyleTableColumn('dischargeSafeguarding', 'Discharge Safeguarding', 7, 'dischargeSafeguarding', 'arrayDict', '', 'medium', 'center', true, true, null, null, null),
    createAgyleTableColumn('diagnosisRequired', 'Diagnosis not Required', 8, 'diagnosisRequired', 'booleanCheckbox', '', 'medium', 'center', true, true, null, null, null),
    createAgyleTableColumn('safeguardingNotRequired', 'Safeguarding not Required', 8, 'safeguardingNotRequired', 'booleanCheckbox', '', 'medium', 'center', true, true, null, null, null),
    createAgyleTableColumn('deactivated', 'Deactivated', 9, 'deactivated', 'booleanCheckbox', '', 'medium', 'center', true, true, null, null, null),
    createAgyleTableColumn('ecdsVersion', 'ECDS Version', 10, 'ecdsVersion', 'string', '', 'medium', 'center', true, true, 100, null, null),
    createAgyleTableColumn('action', 'Action', 11, null, 'action', 'notSort', 'medium', 'center', true, false, null, null, null),
];

const DischargeTemplate = () => {
    const classes = useAgyleConfigStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    // Selectors
    const defaultEcdsVer = useSelector(defaultEcdsVersion);

    const [isModalOpen, openModal, closeModal] = useOpenState(false, true);
    const [editTemplate, setEditTemplate] = useState(null);

    const [version, setVersion] = useState(defaultEcdsVer);
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showOnlyActive, setShowOnlyActive] = useState(true);

    const [isDeleteModalOpen, openDeleteModal, closeDeleteModal] = useOpenState(false, true);

    useEffect(() => {
        if (version) {
            reload();
        } else {
            setList([]);
        }
    }, [version, showOnlyActive]);

    const reload = () => {
        setLoading(true);
        API.getDischargeTemplates(version)
            .then((res) => {
                const data = res?.__data || [];
                setList(data.filter(t => !showOnlyActive || !t?.deactivated));
                refreshUserTemplates();
            })
            .catch((err) => {
                console.log(err);
                dispatch(setSnackbar({type: 'error', message: t('Failed to load discharge templates')}));
                setList([]);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleDeleteItem = (item) => {
        setEditTemplate(item);
        openDeleteModal();
    };

    const handleEditItem = (item) => {
        setEditTemplate(item);
        openModal();
    };

    const refreshUserTemplates = () => {
        const version = dispatch(getEcdsVersion(moment()));
        API.getDischargeTemplates(version)
            .then(templateRes => {
                if (templateRes && templateRes.__data) {
                    dispatch(setDischargeTemplate(templateRes.__data?.filter(t => !t.deactivated)));
                }
            })
            .catch((error) => {
                console.error('Discharge Templates ver and error', version, error);
            });
    };

    const handleSubmit = (body) => {
        handleCloseModal();
        setLoading(true);
        API.updateDischargeTemplates(body)
            .then(() => {
                dispatch(setSnackbar({type: 'success', message: t('Discharge template saved')}));
            })
            .catch((err) => {
                console.error(err);
                dispatch(setSnackbar({type: 'error', message: t('Failed to save discharge template')}));
            })
            .finally(() => reload());
    };

    const handleDeleteTemplate = () => {
        const clone = cloneDeep(editTemplate);
        clone.deactivated = true;
        setLoading(true);

        API.updateDischargeTemplates(clone)
            .then(() => {
                dispatch(setSnackbar({type: 'success', message: t('Discharge template deleted')}));
            })
            .catch((err) => {
                console.error(err);
                dispatch(setSnackbar({type: 'error', message: t('Failed to delete discharge template')}));
            })
            .finally(() => reload());

        handleCloseModal();
    };

    const handleCloseModal = () => {
        closeModal();
        closeDeleteModal();
        setEditTemplate(null);
    };

    return (
        <>
            <div className={classes.header}>
                <AgyleSwitch
                    label={t('Show only active')}
                    value={showOnlyActive}
                    onChange={(e) => setShowOnlyActive(e.target.checked)}
                />

                <AgyleSelectField
                    label={t('ECDS Version')}
                    onChange={setVersion}
                    options={ECDS_VERSION}
                    value={version}
                    fullWidth={false}
                    noBorder={false}
                    withNewBackground
                    style={{width: 200}}
                    size="small"
                />

                <AgyleButton
                    endIcon={<AddIcon/>}
                    onClick={openModal}
                    variant="contained"
                >
                    {t('Create new')}
                </AgyleButton>
            </div>

            <Paper className={classes.paper}>
                {loading && <AgyleLoadingSpinner/>}
                {!loading &&
                    <AgyleTable
                        columns={columnsTable}
                        data={list}
                        deleteItem={handleDeleteItem}
                        editItem={handleEditItem}
                        disableFilter
                    />
                }
            </Paper>

            <DischargeTemplateModal
                open={isModalOpen}
                onClose={handleCloseModal}
                editItem={editTemplate}
                onSave={handleSubmit}
                defaultEcdsVer={version}
            />

            <MessageBox
                open={isDeleteModalOpen}
                title={t('Remove Discharge Template')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                warning
                onAccept={handleDeleteTemplate}
                onClose={handleCloseModal}
                message={t('Are you sure you want to remove this discharge template?')}
            />
        </>
    );
};

export default DischargeTemplate;